// src/app/components/shared/advanced-vocabulary.tsx
//
// The four marks the ADVANCED tier shares: the orb, the capability pill, the
// tool-step chip and the destination mark.
//
// Source: design_evolution/handoff-advanced §0.5, §1.1, §1.2, §2.1.
//
// ── Why they are shared components rather than three copies ────────────────
//
// The package's own diagnosis of Upmode Agent, Brand Publisher and AI
// Workflows is that they read as a different product because all three paint
// generic Tailwind hex. That is one problem, and the spec's own instruction —
// "the orb ... identical at every size. Do not substitute a 2-stop gradient" —
// is a rule about consistency ACROSS modules. A rule like that cannot survive
// as three separate implementations; the fourth one always gets two stops.
//
// Each of these is a composition of kit primitives and tokens rather than a new
// kit component, the same argument `components/shared/chip.tsx` was written
// under in Phase 6, and each is recorded in `design/MISSING.md`.

import type { ReactNode } from "react";
import {
  Check,
  Database,
  FileCode2,
  Globe,
  Linkedin,
  LoaderCircle,
  Rocket,
  Sparkles,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Icon } from "@/app/components/kit";

/* ═══ the orb ═══════════════════════════════════════════════════════════════ */

export type OrbSize = "xs" | "sm" | "md" | "lg";

const ORB_ICON: Record<OrbSize, "sm" | "md" | "lg"> = {
  xs: "sm",
  sm: "sm",
  md: "md",
  lg: "lg",
};

/**
 * The AI identity mark. One three-stop gradient and one glow at every size.
 *
 * `as="button"` gives the send control, which is the same mark doing the same
 * job: handing the page to the model.
 */
export function Orb({
  size = "md",
  as = "span",
  label,
  ...rest
}: {
  size?: OrbSize;
  as?: "span" | "button";
  label?: string;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const className = `upm-orb upm-orb--${size}`;
  const glyph = <Icon icon={Sparkles} size={ORB_ICON[size]} />;
  if (as === "button") {
    return (
      <button type="button" className={className} aria-label={label} title={label} {...rest}>
        {glyph}
      </button>
    );
  }
  return (
    <span className={className} aria-hidden="true">
      {glyph}
    </span>
  );
}

/** The orb wrapping a caller's own glyph — the send control uses ArrowRight. */
export function OrbButton({
  size = "md",
  icon,
  label,
  spinning,
  ...rest
}: {
  size?: OrbSize;
  icon: LucideIcon;
  label: string;
  spinning?: boolean;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      className={`upm-orb upm-orb--${size}`}
      aria-label={label}
      title={label}
      {...rest}
    >
      <Icon
        icon={spinning ? LoaderCircle : icon}
        size={ORB_ICON[size]}
        className={spinning ? "upm-orb__spin" : undefined}
      />
    </button>
  );
}

/* ═══ capability pills ══════════════════════════════════════════════════════
   What an agent may touch. DERIVED from the agent's own fields at every call
   site — see `capabilitiesOf` — because a pill saying "Web search" beside an
   agent whose `tools_enabled.web_search` is false is a claim about another
   object checked never, and that is the defect this codebase names as its
   most common. */

export type Capability = "data" | "web" | "publish";

const CAP_META: Record<Capability, { label: string; icon: LucideIcon }> = {
  data: { label: "Upmode data", icon: Database },
  web: { label: "Web search", icon: Globe },
  publish: { label: "Publish", icon: Rocket },
};

export function CapabilityPill({ cap, large }: { cap: Capability; large?: boolean }) {
  const meta = CAP_META[cap];
  return (
    <span className={`upm-cap upm-cap--${cap}${large ? " upm-cap--lg" : ""}`}>
      <Icon icon={meta.icon} size="sm" />
      {meta.label}
    </span>
  );
}

/**
 * Every agent reads Upmode data, so `data` carries no information on a rail
 * card where every card would have it. The rail asks for the DIFFERENTIATING
 * set; the thread header asks for the full set.
 */
export function capabilitiesOf(
  agent: { tools_enabled?: { web_search?: boolean }; publish_destinations?: string[] | null } | null,
  { differentiatingOnly = false }: { differentiatingOnly?: boolean } = {},
): Capability[] {
  const out: Capability[] = differentiatingOnly ? [] : ["data"];
  if (agent?.tools_enabled?.web_search) out.push("web");
  if ((agent?.publish_destinations ?? []).length > 0) out.push("publish");
  return out;
}

/** The tone an agent's icon chip takes, from the same capabilities. */
export function agentTone(caps: Capability[]): Capability {
  if (caps.includes("publish")) return "publish";
  if (caps.includes("web")) return "web";
  return "data";
}

/* ═══ the tool-step chip ════════════════════════════════════════════════════ */

export function ToolStep({ label, running }: { label: ReactNode; running: boolean }) {
  return (
    <span className="upm-toolstep" data-state={running ? "running" : "done"}>
      <Icon icon={running ? LoaderCircle : Check} size="sm" />
      {label}
    </span>
  );
}

/* ═══ destinations ══════════════════════════════════════════════════════════
   Exactly three, everywhere in the product. The vendor brand colours
   (#2962FF, #21759B, #0A66C2) are gone: three vendor blues with no dark form
   is three more colours to theme, and the destination's NAME is beside the
   mark on every row this appears on. Same argument the publication-history
   rows settled in Phase 6. */

export type DestinationId = "hashnode" | "wordpress" | "linkedin";

export const DEST_META: Record<
  DestinationId,
  { label: string; blurb: string; icon: LucideIcon; tone: string }
> = {
  hashnode: {
    label: "Hashnode",
    blurb: "Long-form article",
    icon: FileCode2,
    tone: "upm-dest--hashnode",
  },
  wordpress: {
    label: "WordPress",
    blurb: "Blog post (HTML)",
    icon: Globe,
    tone: "upm-dest--wordpress",
  },
  linkedin: {
    label: "LinkedIn",
    blurb: "Short-form post",
    icon: Linkedin,
    tone: "upm-dest--linkedin",
  },
};

export function DestinationMark({ id, size = "md" }: { id: string; size?: "sm" | "md" }) {
  const meta = DEST_META[id as DestinationId];
  if (!meta) return null;
  return (
    <span className={meta.tone} style={{ display: "inline-flex" }}>
      <Icon icon={meta.icon} size={size} />
    </span>
  );
}

export function DestinationDot({ id }: { id: string }) {
  const meta = DEST_META[id as DestinationId];
  if (!meta) return null;
  return <span className={`upm-dest-dot ${meta.tone}`} aria-hidden="true" />;
}

export function destinationLabel(id: string): string {
  return DEST_META[id as DestinationId]?.label ?? id;
}
